import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { graphql } from "babel-plugin-relay/macro";
import { useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { RecordSourceSelectorProxy } from "relay-runtime";
import { InternationalizationTab_brand$key } from "./__generated__/InternationalizationTab_brand.graphql";
import CardSection from "../../../components/CardSection";
import LeftRight from "../../../components/LeftRight";
import SaveButton, { SavedState } from "../../../components/SaveButton";
import useBrandLanguages from "../../../hooks/useBrandLanguages";
import useBrandAppConfigUpdate2Mutation from "../../../mutations/useBrandAppConfigUpdate2Mutation";
import { KinnLanguageCode, LANGUAGE_MAP } from "../../../utils/languageMap";

const brandFragment = graphql`
  fragment InternationalizationTab_brand on BrandType {
    id
    ...useBrandLanguages_brand
    appConfig {
      id
    }
  }
`;

type InternationalizationTabProps = {
  brand: InternationalizationTab_brand$key;
};

const InternationalizationTab = ({
  brand: brandKey,
}: InternationalizationTabProps) => {
  const brand = useFragment(brandFragment, brandKey);
  const [updateBrandAppConfig, isMutationInFlight] =
    useBrandAppConfigUpdate2Mutation();
  const languages = useBrandLanguages({
    brand,
    nullIfLocalizationDisabled: false,
  })!;

  // Local state for pending changes
  const [localLanguages, setLocalLanguages] = useState(
    languages.supportedLanguages
  );
  const [
    localEnableLanguageSpecificContent,
    setLocalEnableLanguageSpecificContent,
  ] = useState(languages.enableLanguageSpecificContent);

  // Menu state for add language
  const [addLanguageAnchorEl, setAddLanguageAnchorEl] =
    useState<null | HTMLElement>(null);
  const addLanguageMenuOpen = Boolean(addLanguageAnchorEl);

  // Get available languages to add (excluding already added ones and future values)
  const availableLanguages = useMemo(() => {
    const addedLanguageCodes = localLanguages.map((lang) => lang.code);
    return Object.entries(LANGUAGE_MAP)
      .filter(
        ([code, language]) =>
          language &&
          code !== "%future added value" &&
          !addedLanguageCodes.includes(code as KinnLanguageCode)
      )
      .map(([_, language]) => language!);
  }, [localLanguages]);

  const defaultLanguage = useMemo(() => {
    return (
      localLanguages.find((lang) => lang.code === "EN") ?? localLanguages[0]
    );
  }, [localLanguages]);

  const saveButtonState: SavedState = useMemo(() => {
    if (isMutationInFlight) {
      return SavedState.SAVING;
    }

    const hasChanges =
      JSON.stringify(localLanguages) !==
        JSON.stringify(languages.supportedLanguages) ||
      localEnableLanguageSpecificContent !==
        languages.enableLanguageSpecificContent;

    return hasChanges ? SavedState.ENABLED : SavedState.SAVED;
  }, [
    localLanguages,
    localEnableLanguageSpecificContent,
    languages.supportedLanguages,
    languages.enableLanguageSpecificContent,
    isMutationInFlight,
  ]);

  // Handle delete language
  const handleDeleteLanguage = (languageCode: KinnLanguageCode) => {
    if (localLanguages.length <= 1) return; // Prevent deleting last language

    const updatedLanguages = localLanguages.filter(
      (lang) => lang.code !== languageCode
    );
    setLocalLanguages(updatedLanguages);

    // If only one language remains, disable language-specific content
    if (updatedLanguages.length <= 1) {
      setLocalEnableLanguageSpecificContent(false);
    }
  };

  // Handle drag and drop reordering
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(localLanguages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalLanguages(items);
  };

  // Handle add language
  const handleAddLanguage = (language: any) => {
    setLocalLanguages([...localLanguages, language]);
    setAddLanguageAnchorEl(null);
  };

  // Handle toggle language-specific content
  const handleToggleLanguageSpecificContent = () => {
    if (localLanguages.length > 1) {
      setLocalEnableLanguageSpecificContent(
        !localEnableLanguageSpecificContent
      );
    }
  };

  const handleSave = () => {
    updateBrandAppConfig(
      {
        id: brand.appConfig.id,
        supportedLanguageCodes: localLanguages.map((lang) => lang.code),
        enableLanguageSpecificContent: localEnableLanguageSpecificContent,
      },
      undefined,
      undefined,
      (store: RecordSourceSelectorProxy) => {
        const brandRecord = store.get(brand.id);
        if (brandRecord == null) {
          return;
        }

        const appConfigRecord = brandRecord.getLinkedRecord("appConfig");
        if (appConfigRecord == null) {
          return;
        }

        appConfigRecord.setValue(
          localLanguages.map((lang) => lang.code),
          "supportedLanguages"
        );
        appConfigRecord.setValue(
          localEnableLanguageSpecificContent,
          "enableLanguageSpecificContent"
        );
      }
    );
  };

  return (
    <Container maxWidth="md">
      <CardSection
        title="Language Settings"
        subtitle="Configure which languages your app supports. The application's static text and resources will be translated to the user's device language."
        content={
          <Stack spacing={3}>
            {/* Supported Languages List */}
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                Supported Languages (drag to reorder)
              </Typography>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="languages">
                  {(provided) => (
                    <Stack
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      spacing={1}
                    >
                      {localLanguages.map((language, index) => {
                        const isDefault =
                          language.code === defaultLanguage.code;
                        return (
                          <Draggable
                            key={language.code}
                            draggableId={language.code}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.8 : 1,
                                }}
                              >
                                <LeftRight
                                  expandLeft={true}
                                  left={
                                    <Stack
                                      direction={{ xs: "column", sm: "row" }}
                                      spacing={1}
                                      alignItems={{
                                        xs: "stretch",
                                        sm: "center",
                                      }}
                                      sx={{ width: "100%", minWidth: 0 }}
                                    >
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{ width: "100%", minWidth: 0 }}
                                      >
                                        <div {...provided.dragHandleProps}>
                                          <DragIndicatorIcon
                                            sx={{
                                              color: "text.secondary",
                                              cursor: "grab",
                                              "&:active": {
                                                cursor: "grabbing",
                                              },
                                            }}
                                          />
                                        </div>
                                        <TextField
                                          value={language.label}
                                          variant="outlined"
                                          size="small"
                                          fullWidth
                                          InputProps={{
                                            readOnly: true,
                                          }}
                                          sx={{
                                            minWidth: 0,
                                            flexGrow: 1,
                                          }}
                                        />
                                      </Stack>
                                      {isDefault && (
                                        <Typography
                                          variant="caption"
                                          color="primary.main"
                                          sx={{
                                            fontWeight: "bold",
                                            alignSelf: {
                                              xs: "flex-start",
                                              sm: "center",
                                            },
                                          }}
                                        >
                                          Default
                                        </Typography>
                                      )}
                                      {/* Delete Button */}
                                      <IconButton
                                        size="small"
                                        sx={{
                                          alignSelf: {
                                            xs: "flex-end",
                                            sm: "center",
                                          },
                                        }}
                                        onClick={() =>
                                          handleDeleteLanguage(language.code)
                                        }
                                        color="error"
                                        disabled={localLanguages.length <= 1}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Stack>
                                  }
                                  right={null}
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>
            </Stack>

            {/* Add Language Button */}
            {availableLanguages.length > 0 && (
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={(event) =>
                    setAddLanguageAnchorEl(event.currentTarget)
                  }
                  disabled={availableLanguages.length === 0}
                >
                  Add Language
                </Button>

                <Menu
                  anchorEl={addLanguageAnchorEl}
                  open={addLanguageMenuOpen}
                  onClose={() => setAddLanguageAnchorEl(null)}
                >
                  {availableLanguages.map((language) => (
                    <MenuItem
                      key={language.code}
                      onClick={() => handleAddLanguage(language)}
                    >
                      {language.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}

            {/* Dynamic Content Localization */}
            {localLanguages.length > 1 && (
              <LeftRight
                expandLeft={true}
                left={
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      Dynamic Content Localization
                    </Typography>
                    <Typography variant="body2">
                      Enables language toggle in the dashboard, through which
                      you can set language-specific content (e.g. for the{" "}
                      <Link href="/build/navigation" target="_blank">
                        Navigation
                      </Link>{" "}
                      section).
                    </Typography>
                  </Stack>
                }
                right={
                  <Switch
                    checked={localEnableLanguageSpecificContent}
                    onChange={handleToggleLanguageSpecificContent}
                    disabled={localLanguages.length <= 1}
                  />
                }
              />
            )}

            {/* Language-specific content info */}
            {localLanguages.length > 1 && (
              <Alert severity="warning">
                <strong>Multiple languages are enabled.</strong> Note that
                dynamic content is not automatically translated.
              </Alert>
            )}
          </Stack>
        }
        actions={
          <SaveButton savedState={saveButtonState} onClick={handleSave} />
        }
      />
    </Container>
  );
};

export default InternationalizationTab;
